import UIComponent from "../../ui/component";

export class BaseTabRenderer extends UIComponent {
    templateCache = new Map();

    render(items = [], pagination = {}) {
        if (!this.element) {
            return;
        }

        const list = this.createListNode();
        items.forEach((item) => {
            list.appendChild(this.createItemNode(item));
        });

        const paginationNode = this.createPaginationNode(pagination);
        const currentList = this.find(`[data-tbx-name="${this.getListDataName()}"]`);
        if (currentList) {
            currentList.replaceWith(list);
        } else {
            this.element.prepend(list);
        }

        const currentPagination = this.find(
            `[data-tbx-name="${this.getPaginationDataName()}"]`
        );
        if (currentPagination) {
            if (paginationNode) {
                currentPagination.replaceWith(paginationNode);
            } else {
                currentPagination.remove();
            }
        } else if (paginationNode) {
            this.element.appendChild(paginationNode);
        }
    }

    createListNode() {
        const list = document.createElement("div");
        list.className = `accordion accordion-flush`;
        list.id = this.getListId();
        list.setAttribute("data-tbx-name", this.getListDataName());
        return list;
    }

    createPaginationNode(pagination) {
        if (!pagination || Number(pagination?.meta?.last_page || 1) <= 1) {
            return null;
        }

        const template = this.getTemplateNode(this.getPaginationTemplateId());
        if (!(template instanceof HTMLTemplateElement)) {
            return null;
        }

        const node = template.content.firstElementChild.cloneNode(true);
        const links = this.buildWindowedPaginationLinks(pagination?.meta ?? {});
        const list = this.getField(node, "paginationList");

        if (!list) {
            return null;
        }

        links.forEach((link) => {
            list.appendChild(this.createPaginationLinkNode(link, this.getPageParamName()));
        });

        return node;
    }

    buildWindowedPaginationLinks(meta) {
        const rawLinks = Array.isArray(meta?.links) ? meta.links : [];
        if (rawLinks.length < 3) {
            return rawLinks;
        }

        const previousLink = rawLinks[0];
        const nextLink = rawLinks[rawLinks.length - 1];
        const pageLinks = rawLinks.slice(1, -1);
        const lastPage = Number(meta?.last_page || 1);
        const currentPage = Number(meta?.current_page || 1);

        if (lastPage <= 1 || pageLinks.length === 0) {
            return rawLinks;
        }

        const linkByPage = new Map();
        pageLinks.forEach((link) => {
            const page = this.extractPageNumber(link);
            if (page !== null) {
                linkByPage.set(page, link);
            }
        });

        const pagesToShow = new Set(this.getVisiblePaginationPages(currentPage, lastPage));

        const visiblePages = Array.from(pagesToShow)
            .filter((page) => Number.isInteger(page) && page >= 1 && page <= lastPage)
            .sort((a, b) => a - b);

        if (visiblePages.some((page) => !linkByPage.has(page))) {
            return rawLinks;
        }

        const windowedLinks = [previousLink];
        let previousPage = null;

        visiblePages.forEach((page) => {
            if (previousPage !== null && page - previousPage > 1) {
                windowedLinks.push(this.createEllipsisPaginationLink());
            }

            const link = linkByPage.get(page);
            if (link) {
                windowedLinks.push(link);
            }

            previousPage = page;
        });

        windowedLinks.push(nextLink);
        return windowedLinks;
    }

    createPaginationLinkNode(link, pageParam) {
        const template = this.getTemplateNode(this.getPaginationLinkTemplateId());
        if (!(template instanceof HTMLTemplateElement)) {
            return document.createElement("li");
        }

        const node = template.content.firstElementChild.cloneNode(true);
        const linkNode = this.getField(node, "paginationLink");
        const label = this.formatPaginationLabel(link.label);
        linkNode.textContent = label;

        if (!link.url) {
            node.classList.add("disabled");
            node.setAttribute("aria-disabled", "true");
            const span = document.createElement("span");
            span.className = "page-link";
            span.textContent = label;
            linkNode.replaceWith(span);
            return node;
        }

        if (link.active) {
            node.classList.add("active");
            node.setAttribute("aria-current", "page");
            const span = document.createElement("span");
            span.className = "page-link";
            span.textContent = label;
            linkNode.replaceWith(span);
            return node;
        }

        const page = this.extractPage(link.url, pageParam);
        linkNode.href = this.buildWebPaginationUrl(pageParam, page);
        linkNode.dataset.page = page ?? "";
        linkNode.dataset.apiUrl = link.url || "";
        return node;
    }

    createEllipsisPaginationLink() {
        return {
            url: null,
            label: "...",
            active: false,
        };
    }

    extractPageNumber(link) {
        const label = this.formatPaginationLabel(link?.label);
        const page = Number.parseInt(label, 10);
        return Number.isInteger(page) ? page : null;
    }

    getVisiblePaginationPages(currentPage, lastPage) {
        if (lastPage <= 7) {
            return Array.from({ length: lastPage }, (_, index) => index + 1);
        }

        // Align with Laravel paginator window:
        // left ellipsis appears starting from page 6.
        if (currentPage <= 5) {
            return [1, 2, 3, 4, 5, 6, lastPage];
        }

        if (currentPage >= lastPage - 4) {
            return [1, lastPage - 5, lastPage - 4, lastPage - 3, lastPage - 2, lastPage - 1, lastPage];
        }

        return [1, currentPage - 1, currentPage, currentPage + 1, lastPage];
    }

    formatPaginationLabel(rawLabel) {
        const label = this.decodeHtmlEntities(String(rawLabel ?? "")).trim();
        const normalized = label.replace(/\s+/g, " ");
        if (!normalized) {
            return "";
        }

        const lowerLabel = normalized.toLowerCase();
        if (
            normalized.includes("«")
            || normalized.includes("‹")
            || lowerLabel.includes("previous")
        ) {
            return "«";
        }

        if (
            normalized.includes("»")
            || normalized.includes("›")
            || lowerLabel.includes("next")
        ) {
            return "»";
        }

        return normalized;
    }

    decodeHtmlEntities(value) {
        const textarea = document.createElement("textarea");
        textarea.innerHTML = value;
        return textarea.value;
    }

    extractPage(url, pageParam) {
        if (!url) {
            return null;
        }

        const parsedUrl = new URL(url, window.location.origin);
        return parsedUrl.searchParams.get(pageParam);
    }

    buildWebPaginationUrl(pageParam, page) {
        const url = new URL(window.location.href);
        const tab = this.getTabByPageParam(pageParam);
        if (tab) {
            url.searchParams.set("tab", tab);
        }

        if (page && Number(page) > 1) {
            url.searchParams.set(pageParam, String(page));
        } else {
            url.searchParams.delete(pageParam);
        }

        return url.toString();
    }

    getTabByPageParam(pageParam) {
        if (pageParam === "invoices_page") {
            return "invoices";
        }

        if (pageParam === "transactions_page") {
            return "transactions";
        }

        return null;
    }

    getTemplateNode(templateId) {
        if (this.templateCache.has(templateId)) {
            return this.templateCache.get(templateId);
        }

        const template = this.find(`#${templateId}`);
        if (!(template instanceof HTMLTemplateElement)) {
            return null;
        }

        const cachedTemplate = template.cloneNode(true);
        this.templateCache.set(templateId, cachedTemplate);
        return cachedTemplate;
    }

    getField(node, fieldName) {
        return node.querySelector(`[data-field="${fieldName}"]`);
    }

    setText(node, fieldName, value) {
        const field = this.getField(node, fieldName);
        if (!field) {
            return;
        }

        field.textContent = value ?? "";
    }

    // abstract-ish methods for child classes
    getListId() {
        throw new Error("getListId() must be implemented");
    }

    getListDataName() {
        throw new Error("getListDataName() must be implemented");
    }

    getPaginationTemplateId() {
        throw new Error("getPaginationTemplateId() must be implemented");
    }

    getPaginationDataName() {
        throw new Error("getPaginationDataName() must be implemented");
    }

    getPageParamName() {
        throw new Error("getPageParamName() must be implemented");
    }

    getPaginationLinkTemplateId() {
        throw new Error("getPaginationLinkTemplateId() must be implemented");
    }
}

